import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Download, Plus, Trash, UploadSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", parameter: "", operator: "lte", limit: "", limit2: "", unit: "", severity: "major", description: "" };
const OPS = [["gt", ">"], ["gte", ">="], ["lt", "<"], ["lte", "<="], ["eq", "="], ["between", "between"]];
const SEVS = ["critical", "major", "minor", "advisory"];

export default function CustomRulesDialog({ open, onOpenChange, industry, onChanged }) {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const uploadRef = useRef(null);

  const load = async () => {
    if (!industry) return;
    try {
      const { data } = await api.get("/validation-rules", { params: { industry } });
      setRules(data.rules || []);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, industry]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.parameter.trim()) return toast.error("Name and parameter are required");
    if (form.limit === "") return toast.error("Limit is required");
    if (!industry) return toast.error("Project industry not resolved yet — close and reopen the dialog");
    setSaving(true);
    try {
      await api.post("/validation-rules", {
        industry: industry, name: form.name.trim(), parameter: form.parameter.trim(), operator: form.operator,
        limit: Number(form.limit), limit2: form.limit2 !== "" ? Number(form.limit2) : null,
        unit: form.unit, severity: form.severity, description: form.description, enabled: true,
      });
      toast.success("Custom rule added — re-run validation to apply");
      setForm(EMPTY);
      await load();
      onChanged?.();
    } catch (e) { toast.error(fmtErr(e), { duration: 7000 }); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    try {
      await api.delete(`/validation-rules/${r.id}`);
      toast.success(`Deleted "${r.name}"`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("industry", industry);
    fd.append("file", file);
    try {
      const { data } = await api.post("/validation-rules/upload", fd);
      toast.success(`Upload complete — ${data.created} rule(s) created${data.errors?.length ? `, ${data.errors.length} invalid` : ""}`, { duration: 6000 });
      (data.errors || []).forEach((x) => toast.error(`Row ${x.row}${x.name ? ` (${x.name})` : ""}: ${x.error}`, { duration: 9000 }));
      await load();
      onChanged?.();
    } catch (err) { toast.error(fmtErr(err), { duration: 8000 }); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="custom-rules-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--c-text)] text-sm">Custom Validation Rules — {industry?.replace(/_/g, " ")}</DialogTitle>
          <DialogDescription className="text-[var(--c-muted)] text-xs">
            Your rules run alongside the built-in validation domains on every validation run, under a "Custom Rules" domain.
            Parameter = any calculation output variable (browse names in Design Engine → Custom Formulas → Browse variables).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex items-center mb-2">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Defined rules ({rules.length})</div>
              <div className="ml-auto flex gap-1.5">
                <a href={`${BASE}/api/validation-rules/template`} download data-testid="vr-template-btn"
                  className="h-7 px-2 rounded-sm border border-[var(--c-border)] text-[10px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
                  <Download size={11} /> Template
                </a>
                <button data-testid="vr-upload-btn" onClick={() => uploadRef.current?.click()}
                  className="h-7 px-2 rounded-sm border border-[var(--c-border)] text-[10px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
                  <UploadSimple size={11} /> Upload Excel/CSV
                </button>
                <input ref={uploadRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onUpload} data-testid="vr-upload-file" />
              </div>
            </div>
            {rules.length === 0 && (
              <div className="text-xs text-[var(--c-muted)] border border-dashed border-[var(--c-border)] rounded-sm p-4" data-testid="vr-empty">
                No custom rules yet — add one below or upload the Excel template.
              </div>
            )}
            {rules.map((r) => (
              <div key={r.id} className="flex items-center gap-2 border border-[var(--c-border)] rounded-sm px-3 py-2 mb-1.5" data-testid={`vr-row-${r.id.slice(0, 8)}`}>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm ${r.severity === "critical" ? "bg-[var(--c-red)]/15 text-[var(--c-red)]" : r.severity === "major" ? "bg-[var(--c-amber)]/15 text-[var(--c-amber)]" : "bg-[var(--c-accent)]/15 text-[var(--c-accent)]"}`}>
                  {r.severity.toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="text-xs text-[var(--c-text)] truncate">{r.name}</div>
                  <div className="font-mono text-[10px] text-[var(--c-muted)] truncate">
                    {r.parameter} {r.operator === "between" ? `between ${r.limit} – ${r.limit2}` : `${r.operator} ${r.limit}`}{r.unit && ` ${r.unit}`}
                  </div>
                </div>
                <button onClick={() => del(r)} data-testid={`vr-delete-${r.id.slice(0, 8)}`}
                  className="ml-auto p-1 text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={13} /></button>
              </div>
            ))}
          </div>

          <div className="border-t border-[var(--c-border)] pt-3" data-testid="vr-form">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">Add rule</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Name</Label>
                <Input data-testid="vr-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Max impedance per client spec" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Parameter (calc output variable)</Label>
                <Input data-testid="vr-parameter-input" value={form.parameter} onChange={(e) => set("parameter", e.target.value)}
                  placeholder="e.g. impedance_pct" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Operator</Label>
                <Select value={form.operator} onValueChange={(v) => set("operator", v)}>
                  <SelectTrigger data-testid="vr-operator-select" className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {OPS.map(([v, l]) => <SelectItem key={v} value={v} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] text-xs">{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Limit{form.operator === "between" ? " (min)" : ""}</Label>
                <Input data-testid="vr-limit-input" type="number" value={form.limit} onChange={(e) => set("limit", e.target.value)}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              {form.operator === "between" && (
                <div className="space-y-1">
                  <Label className="text-[10px] text-[var(--c-muted)]">Limit (max)</Label>
                  <Input data-testid="vr-limit2-input" type="number" value={form.limit2} onChange={(e) => set("limit2", e.target.value)}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
                  <SelectTrigger data-testid="vr-severity-select" className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {SEVS.map((s) => <SelectItem key={s} value={s} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] text-xs">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Unit</Label>
                <Input data-testid="vr-unit-input" value={form.unit} onChange={(e) => set("unit", e.target.value)}
                  placeholder="%, kV, W…" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Input data-testid="vr-desc-input" value={form.description} onChange={(e) => set("description", e.target.value)}
                placeholder="Description (optional)" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs flex-1" />
              <Button size="sm" data-testid="vr-save-btn" onClick={save} disabled={saving}
                className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
                <Plus size={13} className="mr-1" /> {saving ? "Saving…" : "Add rule"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
